import React, { FC, PropsWithChildren } from 'react';

import { getUsernameCookie } from '@lucid/utils';
import { SiteNav, ComponentImage, SiteNavOptions } from '@lucid/components-react';

import { useMenuProductSuite, useMenuTopNavigation } from 'hooks/use-menu';
import useNavButtons, { placeholderButtons, placeholderLinks } from 'hooks/use-nav-buttons';
import type { I18n } from 'types/types';

export interface NavbarOptionalProps extends SiteNavOptions {
  /** The Drupal Node ID */
  nid?: string;
  /** The page type. */
  pageType?: string;
}

export interface NavbarProps extends NavbarOptionalProps {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** The i18n translation data. */
  i18nData: I18n.DataEdges;
  /** The logo to be displayed in the Navbar. */
  logo: ComponentImage;
  /** The language currently set for the page context. */
  languageKey: I18n.AvailableLanguagesKey;
  /** Checks if we should hide the signup button. */
  shouldHideSignUpButton?: boolean;
  /** The text for the additional `Nav` link within the dropdown. */
  viewMoreText?: string;
}

const Navbar: FC<PropsWithChildren<NavbarProps>> = ({
  buttonAriaLabel,
  entityTranslations = [],
  i18nData,
  languageKey,
  logo,
  shouldHideSignUpButton,
  viewMoreText,
  ...rest
}) => {
  const navbarMenu = useMenuTopNavigation(languageKey);
  const productSuiteMenu = useMenuProductSuite(languageKey);
  const username = getUsernameCookie();
  const initialNavButtons = useNavButtons(username, shouldHideSignUpButton);

  const allNavMenu = [...productSuiteMenu, ...navbarMenu];
  return (
    <SiteNav
      {...rest}
      isSticky
      willHaveShadowOnScroll
      buttonAriaLabel={buttonAriaLabel}
      buttons={initialNavButtons.navButtons}
      entityTranslations={entityTranslations}
      i18nData={i18nData}
      items={allNavMenu}
      logo={logo}
      placeholderButtons={placeholderButtons}
      placeholderLinks={placeholderLinks}
      shouldHideSignUpButton={shouldHideSignUpButton}
      viewMoreText={viewMoreText}
    />
  );
};

export default Navbar;
